import { Button, Modal, Table } from 'antd';
import i18next from 'i18next';
import * as Moment from 'moment';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateRangeFilter } from 'src/common/components/table-filters/date-range-filter';
import { emptyTableModel, getDateFormat } from 'src/common/utils';
import { formatMessage } from 'src/core/services/http.service';
import { Query } from 'src/core/stores/data-store';
import { TableModel, TableView } from 'src/core/ui/collections/table';
import { container } from 'src/inversify.config';
import { ScopeLogDescendant, ScopeLogSummary, ScopeLogType, ScopeLogsStore } from 'src/stores/dev-logs-store';

const ScopeLogsPage: FC = () => {
  const { t } = useTranslation();
  const [query, setQuery] = useState({
    searchQuery: '',
    orderBy: [{ field: 'Date', direction: 'Descending', useProfile: false }],
    skip: 0,
    take: 10,
  } as Query);

  const [sourceModalVisible, setSourceModalVisible] = useState(false);
  const [descendantsModalVisible, setDescendantsModalVisible] = useState(false);

  const [selectedLog, setSelectedLog] = useState<ScopeLogSummary>({
    date: new Date(),
    previousHierarchy: '',
    previousParentId: '',
    source: '',
    currentHierarchy: '',
    currentParentId: '',
    id: '',
    scopeId: '',
    scopeLogType: ScopeLogType.Delete,
    scopeLogDescendants: [],
  });

  const logsStore = container.get(ScopeLogsStore);
  const logsState = logsStore.state;

  useEffect(
    () => {
      logsStore.load(query);
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const tableModel = {
    query,
    columns: [
      {
        sortable: true,
        searchable: false,
        filter: <DateRangeFilter />,
        fiterField: 'Date',
        field: 'Date',
        title: t('Timestamp'),
        renderer: (data) => <span>{Moment.utc(data.date).local().locale(i18next.language).format(`${getDateFormat()}, hh:mm a`)}</span>,
      },
      {
        sortable: false,
        searchable: false,
        sortField: 'Id',
        useProfile: true,
        field: 'Id',
        title: t('Id'),
        renderer: (data) => <span>{data.id}</span>,
      },
      {
        sortable: false,
        searchable: false,
        sortField: 'ScopeId',
        useProfile: true,
        field: 'ScopeId',
        title: t('ScopeId'),
        renderer: (data) => <span>{data.scopeId}</span>,
      },
      {
        sortable: false,
        searchable: false,
        sortField: 'CurrentParentId',
        useProfile: true,
        field: 'CurrentParentId',
        title: t('CurrentParentId'),
        renderer: (data) => <span>{data.currentParentId}</span>,
      },
      {
        sortable: false,
        searchable: false,
        sortField: 'PreviousParentId',
        useProfile: true,
        field: 'PreviousParentId',
        title: t('PreviousParentId'),
        renderer: (data) => <span>{data.previousParentId}</span>,
      },
      {
        sortable: false,
        searchable: false,
        field: 'CurrentHierarchy',
        title: t('CurrentHierarchy'),
        renderer: (data) => <span>{data.currentHierarchy}</span>,
      },
      {
        sortable: false,
        searchable: false,
        field: 'PreviousHierarchy',
        title: t('PreviousHierarchy'),
        renderer: (data) => <span>{data.previousHierarchy}</span>,
      },
      {
        sortable: false,
        searchable: false,
        field: 'ScopeLogType',
        title: t('ScopeLogType'),
        renderer: (data) => <span>{data.scopeLogType}</span>,
      },
      {
        sortable: false,
        searchable: false,
        field: 'Source',
        title: t('Source'),
        renderer: (data) => (
          <Button
            type="dashed"
            className="solid-button"
            onClick={() => {
              setSelectedLog(data);
              setSourceModalVisible(true);
            }}
          >
            {t('ViewSource')}
          </Button>
        ),
      },
      {
        sortable: false,
        searchable: false,
        field: 'ScopeLogDescendants',
        title: t('Descendants'),
        renderer: (data) => (
          <Button
            type="dashed"
            className="solid-button"
            onClick={() => {
              setSelectedLog(data);
              setDescendantsModalVisible(true);
            }}
          >
            {t('ViewDescendants')}
          </Button>
        ),
      },
    ],
    data: { ...logsState.value, isBusy: logsState.value.isBusy },
  } as TableModel<ScopeLogSummary>;

  return (
    <>
      <TableView
        rowKey="id"
        canCreateNew={false}
        canDelete={false}
        canEdit={false}
        canSort
        onQueryChanged={(query: Query) => {
          setQuery(query);
          logsStore.load(query);
        }}
        onRefresh={() => logsStore.load(query)}
        model={logsState.errorMessage.value ? emptyTableModel : tableModel}
        error={logsState.errorMessage.value && formatMessage(logsState.errorMessage.value)}
      />
      <Modal
        width={600}
        title={Moment.utc(selectedLog.date).local().locale(i18next.language).format(`${getDateFormat()}, hh:mm a`)}
        open={sourceModalVisible}
        onOk={() => setSourceModalVisible(false)}
        onCancel={() => setSourceModalVisible(false)}
        cancelButtonProps={{ style: { visibility: 'hidden', display: 'none' } }}
      >
        <div style={{ height: '400px', overflow: 'scroll' }}>{selectedLog?.source}</div>
      </Modal>
      <Modal
        width={900}
        title={t('Descendants')}
        open={descendantsModalVisible}
        onOk={() => setDescendantsModalVisible(false)}
        onCancel={() => setDescendantsModalVisible(false)}
        cancelButtonProps={{ style: { visibility: 'hidden', display: 'none' } }}
      >
        <Table
          dataSource={selectedLog.scopeLogDescendants}
          pagination={{ defaultPageSize: 10, responsive: true }}
          columns={[
            {
              title: <>{t('Id')}</>,
              render: (data: ScopeLogDescendant) => <span>{data.id}</span>,
            },
            {
              title: <>{t('ScopeId')}</>,
              render: (data: ScopeLogDescendant) => <span>{data.scopeId}</span>,
            },
            {
              title: <>{t('CurrentParentId')}</>,
              render: (data: ScopeLogDescendant) => <span>{data.currentParentId}</span>,
            },
            {
              title: <>{t('CurrentHierarchy')}</>,
              render: (data: ScopeLogDescendant) => <span>{data.currentHierarchy}</span>,
            },
            {
              title: <>{t('PreviousHierarchy')}</>,
              render: (data: ScopeLogDescendant) => <span>{data.previousHierarchy}</span>,
            },
          ]}
        ></Table>
      </Modal>
    </>
  );
};

export default ScopeLogsPage;
